import React from 'react';
import PropTypes from 'prop-types';
import {
  MotifButton,
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifFileUploader,
  MotifTextLink,
} from '@ey-xd/motif-react';
import * as Constants from '../../constants/other';
import * as trsActions from '../../actions/trsActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from '../../common/withRouter';
import { Link } from 'react-router';

class TRSQuarterlyDataUploadModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fileStatuses: [],
      validationMessages: '',
      clientID: 0,
      fileUploadId: 0,
    };
    this.onDrop = this.onDrop.bind(this);
    this.onDropRejected = this.onDropRejected.bind(this);
    this.renderUploadProgress = this.renderUploadProgress.bind(this);
    this.onCloseUploadTRSModal = this.onCloseUploadTRSModal.bind(this);
  }

  componentWillUnmount() {
    // Ensure scroll is restored if component unmounts
    document.body.style.overflow = 'auto';
  }

  /**
   * Handles the file drop event.
   * @param {Array} acceptedFiles - The accepted files.
   * @param {Array} rejectedFiles - The rejected files.
   */
  onDrop(acceptedFiles) {
    const addedStatuses = [];
    // Handle accepted files
    if (acceptedFiles && acceptedFiles.length === 1) {
      let acceptedFile = acceptedFiles[0];

      addedStatuses.push({
        file: acceptedFile,
        status: 'IN_PROGRESS',
      });
      this.setState({ fileStatuses: addedStatuses });

      this.props.trsActions
        .uploadTRSQuarterlyTemplate(
          this.props.clientId,
          acceptedFiles[0],
          this.props.periodId
        )
        .then((response) => {
          const addedStatuses = [];

          if (response && response.success) {
            addedStatuses.push({
              file: acceptedFile,
              status: 'UPLOADED',
            });
            this.setState({
              fileStatuses: addedStatuses,
              clientID: response.clientID,
            });
          } else if (
            response &&
            response.success === false &&
            response.fileUploadId <= 0
          ) {
            addedStatuses.push({
              file: acceptedFile,
              status: 'ERROR',
            });
            this.setState({
              fileStatuses: addedStatuses,
              validationMessages: response.displayMessage,
            });
          } else if (
            response &&
            response.success === false &&
            response.fileUploadId > 0
          ) {
            addedStatuses.push({
              file: acceptedFiles[0],
              status: 'VALIDATION_ERROR',
            });
            this.setState({
              fileStatuses: addedStatuses,
              validationMessages: response.displayMessage,
              clientID: response.clientID,
              fileUploadId: response.fileUploadId,
            });
          } else if (
            response &&
            response.errorResponse &&
            response.errorResponse.message
          ) {
            addedStatuses.push({
              file: acceptedFile,
              status: 'ERROR',
            });
            this.setState({
              fileStatuses: addedStatuses,
              validationMessages: response.errorResponse.message,
            });
          }
        })
        .catch((error) => {
          const errorStatuses = [];
          errorStatuses.push({
            file: acceptedFile,
            status: 'ERROR',
          });
          let errorMessage =
            'An error occurred while uploading. Please try again.';
          if (error && error.message) {
            errorMessage = error.message;
          }
          this.setState({
            fileStatuses: errorStatuses,
            validationMessages: errorMessage,
          });
        });
    }
  }

  onDropRejected(rejectedFiles) {
    const addedStatuses = [];

    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      // Check if all rejected files are due to too many files (this is specific to the dropping of multiple files)
      if (
        rejectedFiles?.every((e) =>
          e?.errors.map((c) => c.code)?.find((err) => err === 'too-many-files')
        )
      ) {
        addedStatuses.push({
          file: null,
          status: 'REJECTED',
        });
      } else {
        // Handle individual rejected files to display the file name
        rejectedFiles.forEach((rejectedFile) => {
          if (
            rejectedFile.errors.find((err) => err.code)?.code ===
              'file-invalid-type' ||
            rejectedFile.errors.find((err) => err.code)?.code ===
              'file-too-large'
          ) {
            addedStatuses.push({
              file: rejectedFile.file,
              status: 'REJECTED',
              errorType: rejectedFile.errors.find((err) => err.code)?.code,
            });
          }
        });
      }
    }

    // Update state with new file statuses
    this.setState({ fileStatuses: addedStatuses });
  }

  onCloseUploadTRSModal() {
    this.props.onClose(this.state.clientID);
  }

  handleQuarterlyServicesFailureExport(clientId, fileUploadId) {
    this.props.trsActions.exportTRSQuarterlyErrorFile(clientId, fileUploadId);
  }

  /**
   * Renders the upload progress.
   * @returns {JSX.Element} The upload progress elements.
   */
  renderUploadProgress() {
    const getClassNameAndMessage = (status, fileSize, errorType) => {
      if (status === 'IN_PROGRESS') {
        return {
          className: 'row bg-secondary text-white',
          message: 'File uploaded successfully. Import is in progress.',
        };
      } else if (status === 'UPLOADED') {
        return {
          className: 'row bg-success text-white',
          message: 'Import successful.',
        };
      } else if (status === 'REJECTED') {
        let displayMessage = '';

        if (errorType === 'file-invalid-type') {
          displayMessage = 'Invalid file extension.';
        } else if (fileSize > Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES) {
          displayMessage =
            'The file size has exceeded max limit of ' +
            Constants.MAX_UPLOAD_FILE_SIZE_IN_MB +
            'MB.';
        }
        return {
          className: 'row bg-danger text-white',
          message: displayMessage,
        };
      } else if (status === 'ERROR') {
        return {
          className: 'row bg-danger text-white',
          message: this.state.validationMessages,
        };
      } else if (status === 'VALIDATION_ERROR') {
        return {
          className: 'row bg-danger text-white',
          message: (
            <>
              Upload failed. Click
              <Link
                href="#"
                data-testid="lnkClickhere"
                onClick={(e) => {
                  e.preventDefault();
                  this.handleQuarterlyServicesFailureExport(
                    this.state.clientID,
                    this.state.fileUploadId
                  );
                }}
              >
                <MotifTextLink variant="alt" id="mdm-text-link">
                  &nbsp;here
                </MotifTextLink>
              </Link>{' '}
              to download the file with validation results.
            </>
          ),
        };
      }

      return { className: 'row bg-warning', message: 'Status Unknown' };
    };

    return this.state.fileStatuses.map((item, index) => {
      if (item.file !== null) {
        const { className, message } = getClassNameAndMessage(
          item.status,
          item.file?.size,
          item?.errorType
        );
        return (
          <div key={index} className={className}>
            <div className="col-md-6" style={{ wordBreak: 'break-all' }}>
              {item.file?.name}
            </div>
            <div className="col-md-5 offset-md-1">{message}</div>
          </div>
        );
      }
    });
  }

  /**
   * Renders the UploadTRSModal component.
   * @returns {JSX.Element} The rendered component.
   */
  render() {
    return (
      <MotifModal
        variant="alt"
        size="lg"
        show={this.props.show}
        onClose={() => this.onCloseUploadTRSModal()}
      >
        <MotifModalHeader
          closeButtonProps={{ onClick: () => this.onCloseUploadTRSModal() }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'center',
            }}
          >
            <span
              className="ml-2"
              data-testid="lblUploadTRSQuarterlyDetailsModalHeader"
            >
              Upload TRS Quarterly Details
            </span>
          </div>
        </MotifModalHeader>
        <MotifModalBody>
          <MotifFileUploader
            onDrop={this.onDrop}
            onDropRejected={this.onDropRejected}
            label="Please upload a single file using the approved TRS quarterly import template."
            maxSize={Constants.MAX_UPLOAD_FILE_SIZE_IN_BYTES}
            accept={{ 'application/vnd.ms-excel': ['.xlsx'] }}
            multiple={false}
            maxFiles={1}
            data-test-id="attachmentFileUpload"
            tabIndex="-1"
            className={
              this.state.fileStatuses?.some(
                (status) => status.status === 'REJECTED'
              )
                ? 'motif-invalid'
                : ''
            }
          />
          <div className="react-modal-content-scrollable mt-3 mb-3">
            {this.renderUploadProgress()}
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            variant="primary"
            className="right"
            type="button"
            data-testid="btnClose"
            onClick={() => this.onCloseUploadTRSModal()}
            disabled={this.state.fileStatuses?.some(
              (status) => status.status === 'IN_PROGRESS'
            )}
          >
            Close
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    );
  }
}

TRSQuarterlyDataUploadModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trsActions: PropTypes.object,
  clientId: PropTypes.number,
};

function mapDispatchToProps(dispatch) {
  return {
    trsActions: bindActionCreators(trsActions, dispatch),
  };
}

function mapStateToProps(state, ownProps) {
  let cid = Number.parseInt(ownProps.router.params.clientId);
  if (isNaN(cid)) {
    cid = 0;
  }
  return {
    clientId: cid,
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TRSQuarterlyDataUploadModal)
);
